import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { BreakCategory } from '@helios/state-management'
import {
  NoteContentValidator,
  NotesValidator,
  checkIsNoteValid,
  notesValidationMessages,
} from '@helios/validators'
import { ToggleStates } from '@northfork/ng-basics'
import { takeUntil } from 'rxjs/operators'
import { Break, BreakNoteHandling } from '../../model/break.model'
import { prefillSelection } from '../../validators/break-code-validation'
import { getNotePresets } from '../../utils/breaks-note-editor-presets'

/**
 * NoteEditor that is a dependency of each breaks modal.
 */
@Component({
  selector: 'hs-note-editor',
  templateUrl: './note-editor.component.html',
  styleUrls: ['./note-editor.component.scss'],
})
export class NoteEditorComponent implements OnInit, OnDestroy {
  /** @internal */
  readonly _noteControl = new FormControl('', [
    NotesValidator(),
    NoteContentValidator(),
  ])
  /** @internal */
  _noteHandlingToggle = new FormControl(true)
  private readonly _destroyed$ = new EventEmitter<void>()
  private _breaks: Break[] = []
  totalBreaks = 0
  areAllBreakSecurityCreation = false

  @Input() showNoteHandlingToggle = true

  // undefined = no consumer has ever bound this input; keep the conservative
  // breaks-derived default (see recomputeNoteValidators) rather than fail open
  // to "optional" for a caller that forgot to wire it up.
  private _noteRequired: boolean | undefined = undefined
  private _emptyAllowedByBreaks = false

  @Input() set noteRequired(value: boolean | undefined) {
    this._noteRequired = value
    // When the note becomes required, mark it expanded so it stays open if it
    // later turns optional (the user can then collapse it themselves).
    if (value === true) this.noteExpanded = true
    this.recomputeNoteValidators()
  }

  @Input() collapsibleOptionalNote = false
  noteExpanded = true

  get isNoteRequired(): boolean {
    return this.collapsibleOptionalNote && this._noteRequired === true
  }
  /** Whether the note textarea is shown vs the collapsed "Add optional note" toggle. */
  get noteVisible(): boolean {
    return (
      !this.collapsibleOptionalNote || this.isNoteRequired || this.noteExpanded
    )
  }
  /** Toggle the optional note open/closed. A required note is always shown. */
  toggleNote() {
    this.noteExpanded = !this.noteExpanded
  }

  _descriptionCodes: string[] = []
  _actionCodes: string[] = []
  action = new FormControl(undefined)
  description = new FormControl(undefined)

  @Input() set breaks(values: Break[]) {
    this._breaks = values
    this.totalBreaks = values.length
    const notes = this.cleanNote(values)

    this.areAllBreakSecurityCreation =
      values.length > 0 &&
      values.every(br => br.categoryName === BreakCategory.SECURITY_CREATION)

    this.setInitialNoteValue(notes)

    // Start expanded if there's already a note (e.g. a prefilled existing note);
    // the user can still collapse it afterward while it's optional.
    if (this._noteControl.value) this.noteExpanded = true

    // Setup notes validator, which ensures notes are 3 words or longer.
    // Allow an empty note for multiple breaks only when they all have valid notes.
    const areAllNotesValid = notes.every(n => checkIsNoteValid(n))
    this._emptyAllowedByBreaks =
      (notes.length > 1 && areAllNotesValid) || this.areAllBreakSecurityCreation
    this.recomputeNoteValidators()

    this.updateActionFromBreaks()
    this.updateDescriptionFromBreaks()
    this.updateCodeValidators()
  }

  @Input() set descriptionCodes(value: string[]) {
    this._descriptionCodes = [...value].sort((a, b) => a.localeCompare(b))
    this.updateDescriptionFromBreaks()
    this.updateCodeValidators()
  }
  get descriptionCodes(): string[] {
    return this._descriptionCodes
  }

  @Input() set actionCodes(value: string[]) {
    this._actionCodes = [...value].sort((a, b) => a.localeCompare(b))
    this.updateActionFromBreaks()
    this.updateCodeValidators()
  }
  get actionCodes(): string[] {
    return this._actionCodes
  }

  @Output() formReady = new EventEmitter<FormGroup>()
  @Output() noteChange = new EventEmitter<string>()
  @Output() actionCodeChange = new EventEmitter<string>()
  @Output() descriptionCodeChange = new EventEmitter<string>()
  @Output() noteHandling = new EventEmitter<BreakNoteHandling>()

  form: FormGroup = new FormGroup({
    note: this._noteControl,
    noteHandlingToggle: this._noteHandlingToggle,
    action: this.action,
    description: this.description,
  })
  validationMessages = { notesValidationMessages }
  presetSelection: string | null = null
  notePresets = getNotePresets()

  noteHandlingToggleStates: ToggleStates = {
    on: 'Add Note',
    off: 'Overwrite Note',
  }

  private recomputeNoteValidators() {
    // Single break AND a caller that bound noteRequired (Hold/Accept always do):
    // that input is authoritative. Otherwise — bulk, or a caller that never
    // bound it — keep the breaks-derived rule (empty allowed only when every
    // break already has a valid note, or all are security-creation).
    const allowEmpty =
      this.totalBreaks === 1 && this._noteRequired !== undefined
        ? !this._noteRequired
        : this._emptyAllowedByBreaks
    this._noteControl.setValidators([
      NotesValidator(allowEmpty),
      NoteContentValidator(),
    ])
    this._noteControl.updateValueAndValidity()
  }

  // A configured-but-unselected code fails validation for a single break, so a
  // consumer that shows the selects would grey the Save button. No current caller
  // does — Hold/Accept never bind actionCodes/descriptionCodes on <hs-note-editor>,
  // and Edit Note has its own code-picker in NoteModalComponent — so this is
  // presently inert; re-verify a real caller before assuming it fires.
  private updateCodeValidators() {
    const single = this.totalBreaks === 1
    this.action.setValidators(
      single && this._actionCodes.length > 0 ? [Validators.required] : [],
    )
    this.description.setValidators(
      single && this._descriptionCodes.length > 0 ? [Validators.required] : [],
    )
    this.action.updateValueAndValidity()
    this.description.updateValueAndValidity()
  }

  ngOnInit() {
    if (this.totalBreaks == 1) this._noteHandlingToggle.setValue(false)
    if (!this.showNoteHandlingToggle) this._noteHandlingToggle.setValue(false)

    this.formReady.emit(this.form)
    this._noteControl.valueChanges
      .pipe(takeUntil(this._destroyed$))
      .subscribe(value => {
        this.noteChange.emit(value)
      })
    // Announce the current (possibly prefilled) note: the prefill setValue runs
    // in the breaks setter before this subscription exists, so its emission is
    // missed — without this, a consumer's note state stays empty until the user
    // edits, which can leave Save wrongly greyed when a note is required.
    this.noteChange.emit(this._noteControl.value)

    this.action.valueChanges
      .pipe(takeUntil(this._destroyed$))
      .subscribe(value => {
        this.actionCodeChange.emit(value)
      })

    this.description.valueChanges
      .pipe(takeUntil(this._destroyed$))
      .subscribe(value => {
        this.descriptionCodeChange.emit(value)
      })

    this._noteHandlingToggle.valueChanges
      .pipe(takeUntil(this._destroyed$))
      .subscribe(shouldAppendNote => {
        if (shouldAppendNote) {
          this.noteHandling.emit(BreakNoteHandling.APPEND)
          return
        }
        this.noteHandling.emit(BreakNoteHandling.REPLACE)
      })
  }

  ngOnDestroy() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  /**
   * Values are coming back with double back slashes (\\). This regex finds and
   * formats them to a new line character (\n).
   */
  cleanNote(breaks: Break[]): (string | undefined)[] {
    return breaks.map(b => b.note?.replace(/(\\r\\n)|(\\n)/g, '\n'))
  }

  /**
   * If there is only one note, set the note value to that note.
   * If there are multiple notes, and they are all the same, set the note value to that note.
   * If there are multiple notes, and they are not all the same, set the note value to an empty string.
   */
  setInitialNoteValue(notes: (string | undefined)[]) {
    if (notes.length === 0) {
      return
    }

    if (notes.length === 1) {
      this._noteControl.setValue(notes[0] ?? '')
      return
    }

    // If the note values are all the same, set the note value to that value.
    const areAllNotesTheSame = notes.every(n => n === notes[0])
    if (areAllNotesTheSame) {
      this._noteHandlingToggle.setValue(false)
      this._noteControl.setValue(notes[0] ?? '')
      return
    }

    // If the note values are not all the same, set the note value to an empty string.
    this._noteControl.setValue('')
  }

  private updateActionFromBreaks() {
    const value = prefillSelection(
      this._breaks.map(b => b.actionCode),
      this._actionCodes,
    )
    if (value) this.action.setValue(value)
  }

  private updateDescriptionFromBreaks() {
    const value = prefillSelection(
      this._breaks.map(b => b.descriptionCode),
      this._descriptionCodes,
    )
    if (value) this.description.setValue(value)
  }
}
